/**
 * Sticky bottom bar shown while ≥1 register row is selected: the bulk actions
 * that apply to the selection, each with how many of the selected documents it
 * would touch. Same shape and placement as the customer list's selection bar.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { PurchaseDocListItem } from "types/InvoiceInbox";
import { Column } from "types/Datatable";
import { usePresenceRailWidth } from "features/presence/use-presence-rail-width";
import UserFilterSelect from "components/user-filter-select/UserFilterSelect";
import { BULK_ACTIONS, BulkAction, BulkActionId, BulkInput, hasCriticalFlag } from "./invoice-inbox-bulk";

const buttonClass =
  "inline-flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-sm transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed max-md:min-h-11";
const toneClass = {
  default: "border-[#E6E1DB] bg-white text-[#3F3227] hover:bg-gray-50",
  primary: "border-[#3F3227] bg-[#3F3227] text-white hover:bg-[#2f251d]",
  critical: "border-red-200 bg-white text-red-700 hover:bg-red-50",
};

// Native checkbox with `indeterminate` support (not a plain React prop) for
// the "select every row on this page" header control.
export function HeaderCheckbox({
  checked,
  indeterminate,
  onChange,
  ariaLabel,
  disabled,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="w-4 h-4 rounded border-gray-300 text-[#3F3227] focus:ring-[#3F3227] disabled:opacity-40"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label={ariaLabel}
    />
  );
}

/**
 * The selection column, shared by the flat register and the month tree so both
 * tick rows the same way. `rows` are the rows the header box selects: the page
 * in the list, one month's page in the tree.
 */
export function buildSelectColumn({
  t,
  rows,
  selectedIds,
  disabled,
  onToggle,
  onSetMany,
}: {
  t: (key: string, options?: any) => string;
  rows: PurchaseDocListItem[];
  selectedIds: Set<string>;
  disabled: boolean;
  onToggle: (id: string) => void;
  onSetMany: (ids: string[], selected: boolean) => void;
}): Column {
  const selectedCount = rows.filter((row) => selectedIds.has(row.id)).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  return {
    key: "_select",
    type: "custom",
    header: (
      <HeaderCheckbox
        checked={allSelected}
        indeterminate={selectedCount > 0 && !allSelected}
        onChange={() => onSetMany(rows.map((row) => row.id), !allSelected)}
        disabled={disabled}
        ariaLabel={t("select_all_rows", { defaultValue: "Select all shown" })}
      />
    ),
    // The whole cell is the hit area, and it swallows the click — otherwise
    // a near-miss beside the box opens the document through the row's rowUrl.
    render: (item: PurchaseDocListItem) => (
      <label className="-m-3 flex cursor-pointer p-3" onClick={(event) => event.stopPropagation()}>
        <input
          type="checkbox"
          className="w-4 h-4 rounded border-gray-300 text-[#3F3227] focus:ring-[#3F3227]"
          checked={selectedIds.has(item.id)}
          disabled={disabled}
          onChange={() => onToggle(item.id)}
          aria-label={t("select_row", { defaultValue: "Select" })}
        />
      </label>
    ),
  };
}

type Props = {
  companyId: string;
  selectedDocs: PurchaseDocListItem[];
  /** The action currently running, if any — every button is disabled meanwhile. */
  running: BulkActionId | null;
  onRun: (action: BulkAction, input: BulkInput) => void;
  onClear: () => void;
};

export default function InvoiceInboxBulkBar({ companyId, selectedDocs, running, onRun, onClear }: Props) {
  const { t } = useTranslation();
  // Keep the bar's right edge on the presence rail's left edge, exactly like
  // the customer list does.
  const railWidth = usePresenceRailWidth(companyId);

  // The bar is fixed, so the page cannot know how tall it is. It reserves its
  // own room below the register (on a phone: two rows, see the order classes).
  const barRef = useRef<HTMLDivElement | null>(null);
  const [barHeight, setBarHeight] = useState(0);
  const hasSelection = selectedDocs.length > 0;
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;
    setBarHeight(bar.offsetHeight);
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => setBarHeight(bar.offsetHeight));
    observer.observe(bar);
    return () => observer.disconnect();
  }, [hasSelection]);

  // An action that asks first (approve, remove, paid by an employee) waits
  // here for the second click.
  const [pending, setPending] = useState<BulkAction | null>(null);
  const [reason, setReason] = useState("");
  const [employeeId, setEmployeeId] = useState("");

  const counts = BULK_ACTIONS.map((action) => ({
    action,
    count: selectedDocs.filter(action.isEligible).length,
  })).filter((entry) => entry.count > 0);

  // The pending confirmation is about a count the person read; if the
  // selection changes under it so the action no longer applies, drop it.
  const pendingCount = pending ? selectedDocs.filter(pending.isEligible).length : 0;
  useEffect(() => {
    if (pending && pendingCount === 0) setPending(null);
  }, [pending, pendingCount]);

  const flaggedAwaitingMe = selectedDocs.filter(
    (doc) => !doc.deletedAt && doc.status === "awaiting_approval" && doc.waitingForMe && hasCriticalFlag(doc)
  ).length;

  if (!hasSelection) return null;

  const label = (action: BulkAction) => t(action.labelKey, { defaultValue: action.defaultLabel });
  const start = (action: BulkAction) => {
    if (action.confirm) {
      setPending(action);
      setReason("");
      setEmployeeId("");
      return;
    }
    onRun(action, { reason: "" });
  };

  return (
    <>
      <div data-testid="invoice-inbox-bulk-bar-room" aria-hidden style={{ height: barHeight }} />
      <div
        ref={barRef}
        data-testid="invoice-inbox-bulk-bar"
        className="fixed left-0 bottom-[var(--phone-bar-height,0px)] z-30 border-t border-[#E6E1DB] bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.08)] transition-[right] duration-200 ease-out"
        style={{ right: railWidth }}
      >
        {/* On a phone the bar is two rows, whatever the selection allows: the
            count with "Clear" on top, the actions in one row that scrolls
            sideways below (`max-md:order-*` sets that order; a wrapping button
            row came to 305 px and covered the cards). While a confirmation is
            open the count row is hidden — the question already carries the
            count and "Cancel" leaves the same way. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
          <div className={clsx("text-sm font-medium text-gray-900 max-md:order-1", pending && "max-md:hidden")}>
            {t("selected_count", { count: selectedDocs.length })}
          </div>

          {!pending && (
            <div className="flex flex-wrap items-center gap-2 max-md:order-3 max-md:w-full max-md:flex-nowrap max-md:overflow-x-auto">
              {counts.map(({ action, count }) => (
                <button
                  key={action.id}
                  type="button"
                  className={clsx(buttonClass, toneClass[action.tone || "default"])}
                  disabled={Boolean(running)}
                  onClick={() => start(action)}
                >
                  {running === action.id && <Loader2 className="h-4 w-4 animate-spin" />}
                  {label(action)}
                  {/* The count only when it differs from the selection — "Send
                      for approval (3)" out of 8 selected says 5 will be skipped. */}
                  {count !== selectedDocs.length && <span className="opacity-70">({count})</span>}
                </button>
              ))}
              {counts.length === 0 && (
                <span className="text-sm text-gray-500">
                  {t("invoice_inbox_bulk_nothing_applies", {
                    defaultValue: "No common action applies to the selected documents.",
                  })}
                </span>
              )}
            </div>
          )}

          {pending && (
            <div className="flex flex-wrap items-center gap-2 max-md:order-3 max-md:w-full">
              <span className="text-sm text-gray-900 max-md:w-full">
                {t("invoice_inbox_bulk_confirm", {
                  defaultValue: "{{action}}: {{count}} document(s)?",
                  action: label(pending),
                  count: pendingCount,
                })}
              </span>
              {pending.needsReason && (
                <input
                  type="text"
                  autoFocus
                  className="w-full rounded-sm border border-[#E6E1DB] px-2 py-1.5 text-base focus:border-[#3F3227] focus:outline-none md:w-72 md:text-sm"
                  placeholder={t("invoice_inbox_delete_reason", { defaultValue: "Why is it being removed?" })}
                  aria-label={t("invoice_inbox_delete_reason", { defaultValue: "Why is it being removed?" })}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              )}
              {pending.needsEmployee && (
                <div className="w-full md:w-72">
                  <UserFilterSelect
                    value={employeeId}
                    onChange={setEmployeeId}
                    label={t("invoice_inbox_payment_source_employee_picker", { defaultValue: "Which employee?" })}
                    labelHidden
                    placeholder={t("invoice_inbox_payment_source_employee_picker", { defaultValue: "Which employee?" })}
                  />
                </div>
              )}
              <button
                type="button"
                className={clsx(buttonClass, toneClass[pending.tone || "primary"], "max-md:flex-1 max-md:justify-center")}
                disabled={
                  Boolean(running) ||
                  (pending.needsReason && !reason.trim()) ||
                  (pending.needsEmployee && !employeeId)
                }
                onClick={() => {
                  const action = pending;
                  setPending(null);
                  onRun(action, { reason: reason.trim(), employeeId: employeeId || undefined });
                }}
              >
                {t("confirm", { defaultValue: "Confirm" })}
              </button>
              <button
                type="button"
                className={clsx(buttonClass, toneClass.default, "max-md:flex-1 max-md:justify-center")}
                onClick={() => setPending(null)}
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>
            </div>
          )}

          {flaggedAwaitingMe > 0 && !pending && (
            <span className="text-sm text-amber-700 max-md:order-4 max-md:w-full">
              {t("invoice_inbox_bulk_flagged_not_approved", {
                defaultValue: "{{count}} with a red warning — approve those from the document itself.",
                count: flaggedAwaitingMe,
              })}
            </span>
          )}

          <div className={clsx("ml-auto max-md:order-2", pending && "max-md:hidden")}>
            <button
              type="button"
              className={clsx(buttonClass, toneClass.default)}
              disabled={Boolean(running)}
              onClick={onClear}
            >
              <X className="h-4 w-4" />
              {t("clear_selection", { defaultValue: "Clear" })}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
